import 'reflect-metadata';

/** A structured summary section. `data` is anything JSON-serializable — the agent reads JSON. */
export interface SummarySection {
  title: string;
  data: unknown;
}

/** Metadata describing one `@AIDataSource` provider method. */
export interface DataSourceMeta {
  /** Stable, globally-unique config id, e.g. 'rx_sales', 'contact_identity'. */
  dataSourceId: string;
  methodName: string;
  /** Human title for the section (used when auto-wrapping `string[]` returns). */
  label?: string;
  /** Feature action that must be enabled for the role, else the source is skipped —
   *  a summary never carries data whose tool the rep cannot see. */
  featureAction?: string;
}

const SOURCES_METADATA_KEY = Symbol('ai:data-sources');

/**
 * Marks an async method as a data source. The method signature is
 * `(ctx: DataSourceContext) => Promise<SummarySection | string[] | null>`. A method may carry
 * more than one `@AIDataSource` (distinct ids) when it serves several entities — it tells them
 * apart via `ctx.dataSourceId`. Provider services register via the `AI_DATA_SOURCES` multi-token.
 */
export function AIDataSource(dataSourceId: string, opts: { label?: string; featureAction?: string } = {}) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const sources: DataSourceMeta[] = Reflect.getMetadata(SOURCES_METADATA_KEY, target.constructor) || [];
    sources.push({ dataSourceId, methodName: propertyKey, label: opts.label, featureAction: opts.featureAction });
    Reflect.defineMetadata(SOURCES_METADATA_KEY, sources, target.constructor);
    return descriptor;
  };
}

/** Every `@AIDataSource`-decorated method on a class (via its constructor). */
export function getRegisteredDataSources(targetClass: any): DataSourceMeta[] {
  return Reflect.getMetadata(SOURCES_METADATA_KEY, targetClass) || [];
}
